import axios from 'axios';
import config from '../config';

/**
 * Save complete onboarding data from all onboarding steps
 * @param {Object} data - Onboarding data
 * @param {string} data.familyStatus - User's family status
 * @param {string} data.anniversary - Anniversary date (if applicable)
 * @param {Array} data.kidsBirthdays - Array of kid objects with names and birthdates
 * @param {Array} data.yartzeits - Array of yartzeit objects with relationships and dates
 * @param {string} data.groupId - The group ID associated with this onboarding process
 * @returns {Promise} - API response
 */
export const saveOnboardingData = async (data) => {
  try {
    // Get the authentication token
    const token = localStorage.getItem('token');
    const headers = { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
    
    const response = await axios.put(
      `${config.API_URL}/users/onboarding`,
      data,
      { headers }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error saving onboarding data:', error);
    throw error;
  }
};

/**
 * Update the current onboarding step for tracking user progress
 * @param {string} groupId - The group ID
 * @param {string} step - The current onboarding step 
 * @returns {Promise} - API response
 */
export const updateOnboardingStep = async (groupId, step) => {
  try {
    // Get the authentication token
    const token = localStorage.getItem('token');
    const headers = { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
    
    const response = await axios.put(
      `${config.API_URL}/users/onboarding-step`,
      { groupId, step },
      { headers }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error updating onboarding step:', error);
    throw error;
  }
};

/**
 * Load user profile data including onboarding information
 * @returns {Promise} - User profile data
 */
export const getUserProfile = async () => {
  try {
    // Get the authentication token
    const token = localStorage.getItem('token');
    const headers = { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
    
    const response = await axios.get(
      `${config.API_URL}/users/profile`,
      { headers }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error loading user profile:', error);
    throw error;
  }
};

/**
 * Log community preview share action
 * @param {Object} shareData - Share data
 * @param {string} shareData.sharedEmail - Email address to share with
 * @param {string} shareData.communityName - Name of the community being shared
 * @param {string} shareData.sessionId - Session ID for tracking
 * @returns {Promise} - API response
 */
export const logPreviewShare = async (shareData) => {
  try {
    // Get the authentication token
    const token = localStorage.getItem('token');
    const headers = { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
    
    const response = await axios.post(
      `${config.API_URL}/onboarding/log-share`,
      shareData,
      { headers }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error logging preview share:', error);
    throw error;
  }
};

/**
 * Log onboarding step completion
 * @param {Object} stepData - Step data
 * @param {string} stepData.sessionId - Session ID for tracking
 * @param {string} stepData.communityName - Name of the community
 * @param {number} stepData.stepReached - Step number reached
 * @param {boolean} stepData.completed - Whether onboarding is completed
 * @returns {Promise} - API response
 */
export const logStepCompletion = async (stepData) => {
  try {
    // Get the authentication token
    const token = localStorage.getItem('token');
    const headers = { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
    
    const response = await axios.post(
      `${config.API_URL}/onboarding/log-step`,
      stepData,
      { headers }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error logging step completion:', error);
    throw error;
  }
};